import type { Booking, BookingStats, PriceCheck, UserProfile, TripStatus } from './types';
import { createBooking, createSegment, createStats, createProfile, createPriceHistory, daysFromNow, daysAgo } from '../test/factories';

// Dev mode scenarios
export type DevModeScenario = 'standard' | 'heavy' | 'new' | 'scenarios' | null;

export function getDevModeScenario(): DevModeScenario {
  // Disable dev mode in production builds for security
  if (import.meta.env.PROD) return null;

  const devParam = new URLSearchParams(window.location.search).get('dev');
  if (!devParam) return null;
  if (devParam === 'standard' || devParam === 'heavy' || devParam === 'new' || devParam === 'scenarios') {
    return devParam;
  }
  // Any truthy value defaults to standard
  if (devParam) return 'standard';
  return null;
}

// Scenario configurations
interface ScenarioConfig {
  upcomingCount: number;
  archivedCount: number;
  description: string;
}

const scenarios: Record<Exclude<DevModeScenario, null>, ScenarioConfig> = {
  standard: {
    upcomingCount: 4,
    archivedCount: 20,
    description: 'Standard usage (4 upcoming, 20 archived)',
  },
  heavy: {
    upcomingCount: 0,
    archivedCount: 100,
    description: 'Power user (100 archived)',
  },
  new: {
    upcomingCount: 0,
    archivedCount: 0,
    description: 'New user (no flights)',
  },
  scenarios: {
    upcomingCount: 6,
    archivedCount: 0,
    description: 'Scenario gallery (one per state)',
  },
};

export function getScenarioDescription(scenario: DevModeScenario): string {
  if (!scenario) return '';
  return scenarios[scenario].description;
}

// ---------------------------------------------------------------------------
// Scenario-specific booking generators
// ---------------------------------------------------------------------------

/** Routes for visual variety across bookings. */
const routeVariety = [
  { origin: 'JFK', destination: 'LAX', airline: 'DL', flight: 'DL400' },
  { origin: 'SFO', destination: 'ORD', airline: 'UA', flight: 'UA921' },
  { origin: 'MIA', destination: 'BOS', airline: 'AA', flight: 'AA318' },
  { origin: 'SEA', destination: 'DFW', airline: 'DL', flight: 'DL762' },
  { origin: 'LAX', destination: 'LHR', airline: 'AA', flight: 'AA136' },
  { origin: 'ORD', destination: 'CDG', airline: 'UA', flight: 'UA987' },
  { origin: 'DEN', destination: 'NRT', airline: 'UA', flight: 'UA837' },
  { origin: 'ATL', destination: 'FCO', airline: 'DL', flight: 'DL64' },
  { origin: 'BOS', destination: 'DXB', airline: 'AA', flight: 'AA250' },
  { origin: 'DFW', destination: 'HKG', airline: 'AA', flight: 'AA137' },
] as const;

function routeOverrides(index: number) {
  const route = routeVariety[index % routeVariety.length];
  return {
    airline_code: route.airline,
    segments: [
      createSegment({
        carrier_code: route.airline,
        flight_number: route.flight,
        origin: route.origin,
        destination: route.destination,
      }),
    ],
  };
}

function generateStandardBookings(): { watching: Booking[]; history: Booking[] } {
  const watching: Booking[] = [
    createBooking('watching', {
      id: 'mock-upcoming-1',
      ...routeOverrides(0),
      total_price_paid: 342,
      cabin_class: 'economy',
      segments: [createSegment({
        booking_id: 'mock-upcoming-1',
        carrier_code: 'DL',
        flight_number: 'DL400',
        origin: 'JFK',
        destination: 'LAX',
        departure_datetime: daysFromNow(14),
      })],
    }),
    createBooking('drop_found', {
      id: 'mock-upcoming-2',
      ...routeOverrides(1),
      total_price_paid: 528,
      cabin_class: 'economy',
      potential_savings: 73,
      latest_price: 455,
      segments: [createSegment({
        booking_id: 'mock-upcoming-2',
        carrier_code: 'UA',
        flight_number: 'UA921',
        origin: 'SFO',
        destination: 'ORD',
        departure_datetime: daysFromNow(21),
      })],
    }),
    createBooking('rebooked', {
      id: 'mock-upcoming-3',
      ...routeOverrides(2),
      total_price_paid: 412,
      cabin_class: 'premium_economy',
      potential_savings: 95,
      segments: [createSegment({
        booking_id: 'mock-upcoming-3',
        carrier_code: 'AA',
        flight_number: 'AA318',
        origin: 'MIA',
        destination: 'BOS',
        departure_datetime: daysFromNow(35),
      })],
    }),
    createBooking('watching', {
      id: 'mock-upcoming-4',
      ...routeOverrides(3),
      total_price_paid: 1890,
      cabin_class: 'business',
      segments: [createSegment({
        booking_id: 'mock-upcoming-4',
        carrier_code: 'DL',
        flight_number: 'DL762',
        origin: 'SEA',
        destination: 'DFW',
        departure_datetime: daysFromNow(45),
      })],
    }),
  ];

  const history: Booking[] = [];
  const cabins = ['economy', 'premium_economy', 'business', 'first'];
  const prices = [250, 380, 520, 290, 610, 445, 330, 710, 195, 840, 1200, 2300, 460, 320, 550, 780, 415, 360, 670, 490];

  for (let i = 0; i < 20; i++) {
    const route = routeVariety[i % routeVariety.length];
    const daysBack = 5 + i * 9; // Spread over ~180 days
    history.push(
      createBooking('departed', {
        id: `mock-archived-${i + 1}`,
        airline_code: route.airline,
        total_price_paid: prices[i],
        cabin_class: cabins[i % 4],
        booking_status: i % 5 === 0 ? 'rebooked' : 'watching',
        potential_savings: i % 5 === 0 ? Math.round(prices[i] * 0.15) : undefined,
        segments: [createSegment({
          booking_id: `mock-archived-${i + 1}`,
          carrier_code: route.airline,
          flight_number: route.flight,
          origin: route.origin,
          destination: route.destination,
          departure_datetime: daysAgo(daysBack),
          arrival_datetime: daysAgo(daysBack - 1),
        })],
      })
    );
  }

  return { watching, history };
}

function generateHeavyBookings(): { watching: Booking[]; history: Booking[] } {
  const history: Booking[] = [];
  const cabins = ['economy', 'premium_economy', 'business', 'first'];

  for (let i = 0; i < 100; i++) {
    const route = routeVariety[i % routeVariety.length];
    const daysBack = 2 + i * 5;
    const price = 200 + (i * 37) % 800;
    history.push(
      createBooking('departed', {
        id: `mock-archived-${i + 1}`,
        airline_code: route.airline,
        total_price_paid: price,
        cabin_class: cabins[i % 4],
        segments: [createSegment({
          booking_id: `mock-archived-${i + 1}`,
          carrier_code: route.airline,
          flight_number: route.flight,
          origin: route.origin,
          destination: route.destination,
          departure_datetime: daysAgo(daysBack),
          arrival_datetime: daysAgo(daysBack - 1),
        })],
      })
    );
  }

  return { watching: [], history };
}

function generateScenariosBookings(): { watching: Booking[]; history: Booking[] } {
  const allStates: TripStatus[] = ['watching', 'drop_found', 'rebooked', 'departed', 'stopped', 'saved'];
  const watching: Booking[] = [];

  allStates.forEach((state, i) => {
    const route = routeVariety[i % routeVariety.length];
    const baseOverrides: Partial<Booking> = {
      id: `mock-scenario-${state}`,
      airline_code: state === 'saved' ? 'B6' : route.airline,
      total_price_paid: 300 + i * 50,
      cabin_class: 'economy',
    };

    // Build segment overrides — 'saved' uses B6, 'departed' uses past dates
    const segOverrides: Partial<import('./types').FlightSegment> = {
      booking_id: `mock-scenario-${state}`,
      carrier_code: state === 'saved' ? 'B6' : route.airline,
      flight_number: state === 'saved' ? 'B6200' : route.flight,
      origin: route.origin,
      destination: route.destination,
    };

    if (state === 'departed') {
      segOverrides.departure_datetime = daysAgo(3);
      segOverrides.arrival_datetime = daysAgo(2);
    } else {
      segOverrides.departure_datetime = daysFromNow(10 + i * 5);
    }

    baseOverrides.segments = [createSegment(segOverrides)];

    watching.push(createBooking(state, baseOverrides));
  });

  return { watching, history: [] };
}

// ---------------------------------------------------------------------------
// Main generator
// ---------------------------------------------------------------------------

interface DevModeData {
  user: {
    id: string;
    email: string;
  };
  watchingBookings: Booking[];
  historyBookings: Booking[];
  stats: BookingStats;
  lastCheckedAt: string;
  profile: UserProfile;
  getPriceHistory: (bookingId: string) => PriceCheck[];
}

export function generateDevModeData(scenario: Exclude<DevModeScenario, null>): DevModeData {
  let watchingBookings: Booking[];
  let historyBookings: Booking[];

  switch (scenario) {
    case 'standard': {
      const result = generateStandardBookings();
      watchingBookings = result.watching;
      historyBookings = result.history;
      break;
    }
    case 'heavy': {
      const result = generateHeavyBookings();
      watchingBookings = result.watching;
      historyBookings = result.history;
      break;
    }
    case 'scenarios': {
      const result = generateScenariosBookings();
      watchingBookings = result.watching;
      historyBookings = result.history;
      break;
    }
    case 'new':
    default:
      watchingBookings = [];
      historyBookings = [];
      break;
  }

  // Sort by departure date
  watchingBookings.sort((a, b) => {
    const aDate = a.segments?.[0]?.departure_datetime || '';
    const bDate = b.segments?.[0]?.departure_datetime || '';
    return aDate.localeCompare(bDate);
  });

  historyBookings.sort((a, b) => {
    const aDate = a.segments?.[0]?.departure_datetime || '';
    const bDate = b.segments?.[0]?.departure_datetime || '';
    return bDate.localeCompare(aDate); // Most recent first
  });

  // Calculate stats
  const allBookings = [...watchingBookings, ...historyBookings];
  const cashSavings = watchingBookings
    .filter((b) => !b.is_award_booking && b.potential_savings)
    .reduce((sum, b) => sum + (b.potential_savings || 0), 0);
  const pointsSavings = watchingBookings
    .filter((b) => b.is_award_booking && b.potential_savings)
    .reduce((sum, b) => sum + (b.potential_savings || 0), 0);

  const stats = createStats({
    total_bookings: allBookings.length,
    active_bookings: watchingBookings.length,
    bookings_with_cheaper_options: watchingBookings.filter((b) => b.has_cheaper_option).length,
    total_potential_savings: cashSavings + pointsSavings / 100,
    total_cash_savings: cashSavings,
    total_points_savings: pointsSavings,
    cash_bookings: allBookings.filter((b) => !b.is_award_booking).length,
    award_bookings: allBookings.filter((b) => b.is_award_booking).length,
    days_monitoring: Math.max(1, Math.floor((Date.now() - new Date(allBookings[0]?.created_at || Date.now()).getTime()) / (24 * 60 * 60 * 1000))),
  });

  // Price history lookup
  const priceHistoryCache = new Map<string, PriceCheck[]>();
  const getPriceHistory = (bookingId: string): PriceCheck[] => {
    if (priceHistoryCache.has(bookingId)) {
      return priceHistoryCache.get(bookingId)!;
    }

    const booking = allBookings.find((b) => b.id === bookingId);
    if (!booking) return [];

    const history = createPriceHistory(bookingId, 8);
    priceHistoryCache.set(bookingId, history);
    return history;
  };

  return {
    user: {
      id: 'dev-user',
      email: 'dev@example.com',
    },
    watchingBookings,
    historyBookings,
    stats,
    lastCheckedAt: new Date(Date.now() - 5 * 60 * 1000).toISOString(), // 5 min ago
    profile: createProfile({
      id: 'dev-user',
      first_name: 'Dev',
      last_name: 'User',
    }),
    getPriceHistory,
  };
}

// Dev mode banner component info
export function getDevModeBannerInfo(scenario: DevModeScenario): {
  label: string;
  counts: string;
} | null {
  if (!scenario) return null;

  const config = scenarios[scenario];
  return {
    label: scenario,
    counts:
      scenario === 'new'
        ? 'empty state'
        : scenario === 'scenarios'
          ? 'one per state'
          : `${config.upcomingCount} active · ${config.archivedCount} archived`,
  };
}
